import React, { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { UnitsExplorer, sanitizeTypePricing, type UnitsExplorerFilters } from "@/components/UnitsExplorer";
import { useStats } from "@/hooks/useStats";
import { useAuth } from "@/app/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Wifi, WifiOff, Zap, AlertTriangle, Plus } from "@/components/icons";
import { ClaimChargerModal } from "@/components/ClaimChargerModal";

const DEFAULTS = {
    sortBy: "name",
    viewMode: "table" as const,
};

function parseStatusParam(raw: string | null): string[] {
    if (!raw) return [];
    return raw.split(",").filter(Boolean);
}

export function DevicesPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { activeDevices, offlineDevices, chargingDevices, unsupportedDevices } = useStats();
    const { isAdmin, isOrgUser } = useAuth();
    // Residential self-claim is for drivers who own their charger personally — not
    // system admins and not organization members (those use the admin assign / org
    // flows). The backend enforces the same rule; hiding the CTA is cosmetic.
    const canClaim = !isAdmin && !isOrgUser;
    const [claimOpen, setClaimOpen] = useState(false);

    const filters = useMemo<UnitsExplorerFilters>(() => {
        // Drop a stale pricing param if Type doesn't include commercial — a URL
        // like ?pricing=paid with no &type=commercial must not silently filter.
        const sane = sanitizeTypePricing(
            parseStatusParam(searchParams.get("type")),
            parseStatusParam(searchParams.get("pricing")),
        );
        return {
            search: searchParams.get("search") ?? "",
            stateFilter: parseStatusParam(searchParams.get("status")),
            sortBy: searchParams.get("sort") ?? DEFAULTS.sortBy,
            viewMode: (searchParams.get("view") as "table" | "vitrine") ?? DEFAULTS.viewMode,
            typeFilter: sane.typeFilter,
            pricingFilter: sane.pricingFilter,
            protocolFilter: parseStatusParam(searchParams.get("protocol")),
        };
    }, [searchParams]);

    const onFiltersChange = useCallback((next: UnitsExplorerFilters) => {
        const params = new URLSearchParams();
        if (next.search) params.set("search", next.search);
        if (next.stateFilter.length) params.set("status", next.stateFilter.join(","));
        if (next.sortBy && next.sortBy !== DEFAULTS.sortBy) params.set("sort", next.sortBy);
        if (next.viewMode && next.viewMode !== DEFAULTS.viewMode) params.set("view", next.viewMode);
        if (next.typeFilter.length) params.set("type", next.typeFilter.join(","));
        if (next.pricingFilter.length) params.set("pricing", next.pricingFilter.join(","));
        if (next.protocolFilter.length) params.set("protocol", next.protocolFilter.join(","));
        setSearchParams(params, { replace: true });
    }, [setSearchParams]);

    const setStatus = useCallback((statuses: string[]) => {
        onFiltersChange({ ...filters, stateFilter: statuses });
    }, [filters, onFiltersChange]);

    const stats = [
        { label: "Active", value: activeDevices, icon: Wifi, color: "text-primary", statuses: ["charging", "standby", "connected", "ready"] },
        { label: "Charging", value: chargingDevices, icon: Zap, color: "text-teal-500", statuses: ["charging"] },
        { label: "Offline", value: offlineDevices, icon: WifiOff, color: "text-muted-foreground", statuses: ["offline"] },
        { label: "Attention", value: unsupportedDevices, icon: AlertTriangle, color: unsupportedDevices > 0 ? "text-rose-500" : "text-muted-foreground", statuses: ["unsupported", "error", "blocked"] },
    ];

    return (
        <div className="space-y-5 pt-[86px]">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <h1 className="text-2xl font-semibold">Devices</h1>
                {canClaim && (
                    <Button onClick={() => setClaimOpen(true)}>
                        <Plus className="h-4 w-4" /> Claim a charger
                    </Button>
                )}
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {stats.map((s) => {
                    const active = s.statuses.length === filters.stateFilter.length &&
                        s.statuses.every((v) => filters.stateFilter.includes(v));
                    return (
                        <Card
                            key={s.label}
                            className={`cursor-pointer transition-colors hover:bg-muted/50 ${active ? "ring-2 ring-primary/40" : ""}`}
                            onClick={() => setStatus(active ? [] : s.statuses)}
                        >
                            <CardContent className="flex items-center gap-3 py-3">
                                <s.icon className={`h-5 w-5 ${s.color}`} />
                                <div>
                                    <div className={`text-2xl font-semibold ${s.color}`}>{s.value}</div>
                                    <div className="text-xs text-muted-foreground">{s.label}</div>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            <UnitsExplorer
                filters={filters}
                onFiltersChange={onFiltersChange}
                canClaim={canClaim}
                onClaim={() => setClaimOpen(true)}
            />

            {canClaim && <ClaimChargerModal open={claimOpen} onOpenChange={setClaimOpen} />}
        </div>
    );
}
